import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Wand2, Loader2, Plus, Trash2 } from "lucide-react";
import { Question } from "@/types/test";
import { useToast } from "@/hooks/use-toast";
import { generateQuestions } from "./utils/questionGeneration";
import { ScrollArea } from "@/components/ui/scroll-area";
import { TestDetails } from "@/hooks/test-creator/useTestDetails";
import { LoadingStateWithProgress } from "../test-details/states/LoadingStateWithProgress";
import { useIsMobile } from "@/hooks/use-mobile";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

// Define types for skill configuration
export interface SkillConfig {
  skill: string;
  difficulty: 'easy' | 'medium' | 'hard' | null;
  counts: {
    multiple_choice: number;
    essay: number;
    audio_response: number;
    video_response: number;
    coding: number;
  };
}

interface SkillBasedGenerationDialogProps {
  details: TestDetails;
  onGenerateQuestions: (questions: Question[]) => void;
  loading?: boolean;
  disabled?: boolean;
}

export const SkillBasedGenerationDialog = ({
  details,
  onGenerateQuestions,
  loading = false,
  disabled = false,
}: SkillBasedGenerationDialogProps) => {
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [skillConfigs, setSkillConfigs] = useState<SkillConfig[]>([]);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [isAIFilling, setIsAIFilling] = useState(false);

  // Total questions count
  const totalQuestions = skillConfigs.reduce((sum, config) => {
    return sum + Object.values(config.counts).reduce((countSum, count) => countSum + count, 0);
  }, 0);

  // Initialize skill configs when dialog opens or details.keySkills changes
  useEffect(() => {
    if (open && details.keySkills && details.keySkills.length > 0) {
      // Create configs for each skill from job details
      const initialConfigs: SkillConfig[] = details.keySkills.map(skill => ({
        skill,
        difficulty: null,
        counts: {
          multiple_choice: 0,
          essay: 0,
          audio_response: 0,
          video_response: 0,
          coding: 0,
        }
      }));
      
      setSkillConfigs(initialConfigs);
    }
  }, [open, details.keySkills]);

  const handleDifficultyChange = (skillIndex: number, difficulty: 'easy' | 'medium' | 'hard' | null) => {
    const newConfigs = [...skillConfigs];
    newConfigs[skillIndex].difficulty = difficulty;
    setSkillConfigs(newConfigs);
  };

  const handleCountChange = (skillIndex: number, questionType: keyof SkillConfig['counts'], value: number) => {
    const newConfigs = [...skillConfigs];
    newConfigs[skillIndex].counts[questionType] = Math.max(0, value); // Ensure non-negative
    setSkillConfigs(newConfigs);
  };

  const handleFillUsingAI = async () => {
    try {
      if (!details.keySkills || details.keySkills.length === 0) {
        toast({
          title: "Missing skills",
          description: "Please add key skills to the test details first.",
          variant: "destructive",
        });
        return;
      }

      setIsAIFilling(true);
      setProgress(10);

      const { data, error } = await supabase.functions.invoke('suggest-question-distribution', {
        body: {
          jobTitle: details.title,
          jobDescription: details.description,
          keyResponsibilities: details.keyResponsibilities,
          yearsOfExperience: details.yearsOfExperience,
          keySkills: details.keySkills,
          maxTotal: 15,
        }
      });

      if (error) throw error;

      const suggestions = (data as any)?.skillConfigs || (data as any)?.suggestions || [];
      if (!Array.isArray(suggestions) || suggestions.length === 0) {
        throw new Error('AI did not return a valid distribution');
      }

      // Create a map of AI suggestions by skill name for easy lookup
      const suggestionMap = new Map();
      suggestions.forEach((s: any) => {
        if (s.skill) {
          suggestionMap.set(s.skill.toLowerCase().trim(), s);
        }
      });

      // Update existing skillConfigs with AI suggestions, keeping original skills
      const updatedConfigs = skillConfigs.map(config => {
        // Try to find matching suggestion (case-insensitive)
        const suggestion = suggestionMap.get(config.skill.toLowerCase().trim());
        
        if (suggestion) {
          return {
            skill: config.skill, // Keep original skill name
            difficulty: suggestion?.difficulty === 'easy' || suggestion?.difficulty === 'medium' || suggestion?.difficulty === 'hard' 
              ? suggestion.difficulty : config.difficulty,
            counts: {
              multiple_choice: Number(suggestion?.counts?.multiple_choice || 0),
              essay: Number(suggestion?.counts?.essay || 0),
              audio_response: 0, // Always 0 as per requirement
              video_response: Number(suggestion?.counts?.video_response || 0),
              coding: Number(suggestion?.counts?.coding || 0),
            }
          };
        } else {
          // If no suggestion found, provide a default distribution
          return {
            skill: config.skill,
            difficulty: config.difficulty ?? null,
            counts: {
              multiple_choice: 0,
              essay: 0,
              audio_response: 0,
              video_response: 0,
              coding: 0,
            }
          };
        }
      });

      const sum = (arr: SkillConfig[]) => arr.reduce((s, c) => s + Object.values(c.counts).reduce((a, b) => a + b, 0), 0);
      const before = sum(updatedConfigs);
      const ensured = ensureMinOnePerSkill(updatedConfigs);
      const limited = trimConfigsToLimit(ensured, 15);
      const after = sum(limited);

      setSkillConfigs(limited);
      setProgress(100);
      toast({ title: 'Filled using AI', description: after < before ? 'Suggestions trimmed to 15 max.' : 'Question distribution suggested.' });
    } catch (e: any) {
      console.error('Fill using AI failed:', e);
      toast({ title: 'AI suggestion failed', description: e.message || 'Please try again later.', variant: 'destructive' });
    } finally {
      setIsAIFilling(false);
      setTimeout(() => setProgress(0), 400);
    }
  };

  // Balanced heuristic helpers for suitable question types per skill
  const getPreferredQuestionType = (skillName: string): keyof SkillConfig['counts'] => {
    const s = (skillName || '').toLowerCase();
    if (/(presentation|public\s*speak|communication|lead(ership)?|client|stakeholder|interpersonal)/.test(s)) {
      return 'video_response';
    }
    if (/(manage|management|project|plan|strategy|analys|analysis|writing|documentation|collab|team|problem.solv|critical.think)/.test(s)) {
      return 'essay';
    }
    if (/(programm|coding|javascript|python|java|react|node|database|sql|algorithm|data.struct|software|developer|frontend|backend|fullstack)/.test(s)) {
      return 'coding';
    }
    // More balanced fallback - cycle between question types rather than defaulting to MCQ
    const hash = skillName.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
    const types: Array<keyof SkillConfig['counts']> = ['multiple_choice', 'essay', 'video_response', 'coding'];
    return types[hash % types.length];
  };

  // Ensure at least 1 question per skill with a suitable default type
  const ensureMinOnePerSkill = (configs: SkillConfig[]): SkillConfig[] => {
    return configs.map((c) => {
      const total = Object.values(c.counts).reduce((a, b) => a + (Number.isFinite(b) ? (b as number) : 0), 0);
      if (total > 0) return c;
      const preferred = getPreferredQuestionType(c.skill);
      const counts = { ...c.counts, multiple_choice: 0, essay: 0, video_response: 0, audio_response: 0, coding: 0 };
      counts[preferred] = 1;
      return { ...c, difficulty: c.difficulty ?? 'medium', counts };
    });
  };

  // Trim to limit while preserving at least 1 per skill and preferably the suitable type
  const trimConfigsToLimit = (configs: SkillConfig[], limit: number) => {
    const cloned = configs.map((c) => ({
      ...c,
      counts: { ...c.counts },
    }));
    const total = (arr: SkillConfig[]) =>
      arr.reduce(
        (sum, cfg) =>
          sum + Object.values(cfg.counts).reduce((a, b) => a + (typeof b === 'number' ? (b as number) : 0), 0),
        0
      );

    let t = total(cloned);
    const order: Array<keyof SkillConfig['counts']> = ['video_response', 'essay', 'coding', 'multiple_choice', 'audio_response'];

    let madeChange = true;
    while (t > limit && madeChange) {
      madeChange = false;
      for (const cfg of cloned) {
        if (t <= limit) break;
        const preferred = getPreferredQuestionType(cfg.skill);
        const skillTotal = Object.values(cfg.counts).reduce((a, b) => a + (b as number), 0);
        for (const type of order) {
          if (t <= limit) break;
          const val = cfg.counts[type] as number;
          const currentSkillTotal = Object.values(cfg.counts).reduce((a, b) => a + (b as number), 0);
          if (currentSkillTotal <= 1) break; // never drop below 1 per skill
          if (val > 0) {
            if (type === preferred && val === 1) {
              // keep at least one preferred type if possible
              continue;
            }
            cfg.counts[type] = val - 1;
            t -= 1;
            madeChange = true;
          }
        }
      }
    }
    return cloned;
  };

  const handleGenerate = async () => {
    try {
      // Validation
      if (totalQuestions < 1) {
        toast({
          title: "Input Error",
          description: "Please specify at least one question to generate",
          variant: "destructive",
        });
        return;
      }
      
      if (totalQuestions > 15) {
        toast({
          title: "Input Error",
          description: "Please limit the total number of questions to 15 or fewer",
          variant: "destructive",
        });
        return;
      }
      
      // Start generation process
      setIsGenerating(true);
      setProgress(10);
      
      // Build the distribution object for the API from skill configs
      const questionsData = skillConfigs.filter(config => {
        // Only include configs that have at least one question
        return Object.values(config.counts).some(count => count > 0);
      });
      
      if (questionsData.length === 0) {
        toast({
          title: "Input Error",
          description: "Please specify at least one question to generate",
          variant: "destructive",
        });
        setIsGenerating(false);
        return;
      }
      
      console.log("Generating questions with config:", questionsData);
      
      // Call the updated generateQuestions function with skill configs
      const generatedQuestions = await generateQuestions(
        details.title,
        details.description,
        details.keyResponsibilities,
        details.yearsOfExperience,
        details.keySkills,
        { skillConfigs: questionsData }
      );
      
      setProgress(100);
      
      if (generatedQuestions && generatedQuestions.length > 0) {
        onGenerateQuestions(generatedQuestions);
        setOpen(false);
        toast({
          title: "Success",
          description: `Generated ${generatedQuestions.length} questions`,
        });
      } else {
        throw new Error('No questions were generated');
      }
    } catch (error: any) {
      console.error('Error generating questions:', error);
      toast({
        title: "Error generating questions",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setIsGenerating(false);
  };

  const questionTypes: Array<keyof SkillConfig['counts']> = [
    'multiple_choice', 'essay', 'video_response', 'audio_response', 'coding'
  ];
  
  const questionTypeLabels: Record<keyof SkillConfig['counts'], string> = {
    multiple_choice: 'MCQ',
    essay: 'Essay',
    video_response: 'Video',
    audio_response: 'Audio',
    coding: 'Code'
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          onClick={() => setOpen(true)}
          disabled={disabled || loading}
          size={isMobile ? "lg" : "default"}
          className={cn(
            isMobile ? "w-full max-w-[280px]" : "",
            "bg-[#E16702] text-white hover:bg-[#C55A02] transition-colors"
          )}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              Generate Questions
              <Wand2 className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent 
        className={cn(
          "bg-card border-none p-0 overflow-hidden", // Remove default padding, prevent spillover
          isMobile ? "w-[95vw] min-h-[32rem] max-h-[90vh]" : "w-[85vw] max-w-6xl min-h-[32rem] max-h-[90vh]" // Responsive sizing
        )}
      >
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0"> {/* Fixed header */}
          <DialogTitle className="text-foreground text-lg sm:text-xl">Configure Question Generation</DialogTitle>
          <DialogDescription className="text-muted-foreground text-sm">
            Select the number of questions to generate for each skill and difficulty level.
          </DialogDescription>
        </DialogHeader>

        {isGenerating ? (
          <div className="flex items-center justify-center flex-1 min-h-[20rem]">
            <LoadingStateWithProgress
              message="Generating questions..."
              progress={progress}
            />
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0"> {/* Allow shrinking */}
            <ScrollArea className="flex-1 max-h-[calc(100vh-20rem)] overflow-auto"> {/* Both vertical and horizontal scrolling */}
              <div className="px-6 py-4 min-w-[48rem]"> {/* Set minimum width to ensure table usability */}
                <div className="rounded-md border">
                  <Table>
                    <TableHeader className="sticky top-0 bg-background z-10"> {/* Sticky header */}
                      <TableRow>
                        <TableHead className="w-[12rem] text-sm font-medium">Skill</TableHead>
                        <TableHead className="w-[8rem] text-sm font-medium">Difficulty</TableHead>
                        {questionTypes.map(qType => (
                          <TableHead key={qType} className="w-[6rem] text-center text-sm font-medium">
                            {questionTypeLabels[qType]}
                          </TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {skillConfigs.map((config, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium">
                            <span className="text-sm">{config.skill}</span>
                          </TableCell>
                          <TableCell>
                            <Select
                              value={config.difficulty || "null"}
                              onValueChange={(value) => handleDifficultyChange(index,
                                value === "null" ? null : value as 'easy' | 'medium' | 'hard'
                              )}
                            >
                              <SelectTrigger className="h-8 text-sm w-[7rem]">
                                <SelectValue placeholder="Select" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="null">Any</SelectItem>
                                <SelectItem value="easy">Easy</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="hard">Hard</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          {questionTypes.map(qType => (
                            <TableCell key={qType} className="text-center">
                              <div className="flex justify-center">
                                <Input
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  value={config.counts[qType] === 0 ? '' : config.counts[qType]}
                                  onChange={(e) => handleCountChange(index, qType, parseInt(e.target.value) || 0)}
                                  className="h-8 w-16 text-center text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </ScrollArea>
            <div className="px-6 py-4 border-t flex-shrink-0 bg-background"> {/* Sticky footer */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="space-y-1">
                  <h3 className="font-medium text-sm">Summary</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Total questions:</span>
                    <Badge variant={totalQuestions > 15 ? "destructive" : "outline"} className="text-xs">
                      {totalQuestions}
                    </Badge>
                  </div>
                  {totalQuestions > 15 && (
                    <p className="text-xs text-destructive">
                      Max 15 questions allowed.
                    </p>
                  )}
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button variant="outline" onClick={handleClose} className="flex-1 sm:flex-none">
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleFillUsingAI}
                    disabled={isAIFilling || isGenerating}
                    className="flex-1 sm:flex-none"
                  >
                    {isAIFilling ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Filling with AI...
                      </>
                    ) : (
                      <>
                        <Wand2 className="mr-2 h-4 w-4" />
                        Fill using AI
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleGenerate}
                    disabled={totalQuestions < 1 || totalQuestions > 15 || isGenerating}
                    className="bg-[#E16702] text-white hover:bg-[#C55A02] transition-colors flex-1 sm:flex-none"
                  >
                    {isGenerating ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      "Generate Questions"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
